import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ShieldCheck, Phone, MessageCircle, MapPin, Clock, Mail, ArrowLeft, CheckCircle2, Download,
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/jyhdvnlr_logo-png-cuadrado.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tienda física Core Colchón — Navalcarnero
const STORE_PHONE_DISPLAY = "910 88 99 62";
const STORE_PHONE_TEL = "+34910889962";
const WHATSAPP_DISPLAY = "+34 630 66 67 34";
const WHATSAPP_LINK = "https://api.whatsapp.com/send?phone=34630666734&text=Quiero%20mi%20cup%C3%B3n%20descuento%20de%20100%E2%82%AC";

export default function Gracias() {
  const location = useLocation();
  const leadId = location.state?.leadId || null;
  const leadName = location.state?.leadName || "";
  const source = location.state?.source || "cheque";
  const isCallback = source === "dudas";
  const voucherUrl = !isCallback && leadId ? `${API}/public/leads/${leadId}/voucher` : null;

  useEffect(() => {
    document.title = "¡Gracias! · Core Colchón";
    // GTM: empuja evento de conversión si está cargado
    if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: "lead_thankyou_view" });
    }
  }, []);

  const handleDownload = () => {
    if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: "voucher_download" });
    }
  };

  return (
    <div data-testid="gracias-page" className="min-h-screen bg-[#F9F8FA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E3DDE8]">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-home-top">
            <img src={LOGO} alt="Core Colchón" className="h-9 w-9 object-contain"/>
            <div>
              <div className="font-display font-bold text-[#2E2638] leading-none">CORE COLCHÓN</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B1538] font-bold">Hecho en España</div>
            </div>
          </Link>
          <Link to="/" data-testid="back-home-link" className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 text-[#5C5366] hover:text-[#8B1538] transition-colors">
            <ArrowLeft size={14}/> Volver a la web
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-10 py-12 sm:py-20">

        {/* Hero confirmación */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#8B1538] rounded-full mb-6 shadow-[0_8px_30px_rgba(139,21,56,0.3)]">
            <CheckCircle2 className="text-white" size={42} strokeWidth={2.5}/>
          </div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">
            Solicitud recibida
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2E2638] leading-tight mb-5">
            {isCallback ? (
              <>¡Gracias{leadName ? `, ${leadName.split(" ")[0]}` : ""}! Te <span className="text-[#8B1538]">llamamos en breve</span>.</>
            ) : (
              <>¡Gracias{leadName ? `, ${leadName.split(" ")[0]}` : ""}! Tu cheque de <span className="text-[#8B1538]">100 €</span> ya está listo.</>
            )}
          </h1>
          <p className="text-lg text-[#5C5366] max-w-2xl mx-auto leading-relaxed">
            {isCallback ? (
              <>Hemos recibido tu solicitud. <strong className="text-[#2E2638]">Un asesor de Core te llamará en menos de 24 h</strong> (dentro del horario de tienda) para resolver todas tus dudas. Mientras tanto, también puedes contactarnos tú directamente.</>
            ) : (
              <>Descárgalo desde aquí mismo y guárdalo en tu móvil. En <strong className="text-[#2E2638]">breves momentos uno de nuestros asesores te llamará</strong> para agendar tu visita a la tienda de Navalcarnero.</>
            )}
          </p>
        </div>

        {/* Descarga del cheque PDF */}
        {voucherUrl && (
          <section className="mb-10 relative overflow-hidden bg-gradient-to-br from-[#2E2638] to-[#1d1726] text-white p-7 sm:p-10 border border-[#2E2638]">
            <div aria-hidden className="absolute -top-24 -right-24 w-72 h-72 bg-[#8B1538]/30 rounded-full blur-3xl"/>
            <div className="relative grid sm:grid-cols-12 gap-6 items-center">
              <div className="sm:col-span-7">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#C9A961] mb-2">Tu cheque</div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold leading-tight mb-2">
                  100 € de descuento en PDF
                </h2>
                <p className="text-white/75 text-sm leading-relaxed max-w-md">
                  Descarga tu cheque ahora y enséñalo (impreso o desde el móvil) cuando vengas a la tienda.
                  Contiene tu código personal de canje.
                </p>
              </div>
              <div className="sm:col-span-5 flex sm:justify-end">
                <a
                  href={voucherUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  onClick={handleDownload}
                  data-testid="download-voucher"
                  className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#8B1538] hover:bg-[#A01840] text-white font-bold uppercase tracking-wider text-sm transition-all shadow-[0_8px_24px_rgba(139,21,56,0.5)] hover:-translate-y-0.5 w-full sm:w-auto"
                >
                  <Download size={18}/> Descargar mi cheque PDF
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Lo que pasará ahora */}
        <section className="bg-white border border-[#E3DDE8] p-7 sm:p-9 mb-10">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#8B1538] mb-4">
            Qué pasará ahora
          </div>
          <ol className="space-y-5">
            {[
              { t: "Descarga tu cheque PDF", d: "Pulsa el botón de arriba y guárdalo en tu móvil. Te valdrá tanto impreso como en pantalla." },
              { t: "Te llama un asesor de Core", d: "Para coordinar la mejor hora de visita y resolver tus dudas antes de ir." },
              { t: "Vas a la tienda en Navalcarnero", d: "Pruebas el colchón, ajustas la firmeza lumbar y aplicamos los 100 € directamente." },
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-4">
                <div className="shrink-0 w-9 h-9 bg-[#F9F8FA] border border-[#E3DDE8] flex items-center justify-center font-display font-bold text-[#8B1538]">
                  {i + 1}
                </div>
                <div>
                  <div className="font-bold text-[#2E2638] text-base">{step.t}</div>
                  <div className="text-sm text-[#5C5366] mt-0.5">{step.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* CTA contacto directo */}
        <section className="mb-10">
          <div className="text-center mb-6">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-2">
              ¿Prefieres adelantarte?
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#2E2638]">
              Escríbenos o llámanos tú directamente
            </h2>
            <p className="text-[#5C5366] mt-2 text-sm sm:text-base">
              Si no quieres esperar la llamada, contacta con nosotros ahora mismo.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <a
              data-testid="cta-whatsapp"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-emerald-600 hover:bg-emerald-700 text-white p-6 transition-all duration-300 shadow-[0_6px_20px_rgba(5,150,105,0.25)] hover:shadow-[0_12px_30px_rgba(5,150,105,0.35)] hover:-translate-y-0.5 flex items-center gap-4"
            >
              <div className="w-14 h-14 bg-white/15 flex items-center justify-center shrink-0">
                <MessageCircle size={26}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-100">WhatsApp</div>
                <div className="font-display text-xl font-bold leading-tight">Abrir chat ahora</div>
                <div className="text-sm text-emerald-50 mt-0.5">Respondemos en horario de tienda</div>
              </div>
            </a>

            <a
              data-testid="cta-call"
              href={`tel:${STORE_PHONE_TEL}`}
              className="group bg-[#8B1538] hover:bg-[#6A0F2A] text-white p-6 transition-all duration-300 shadow-[0_6px_20px_rgba(139,21,56,0.3)] hover:shadow-[0_12px_30px_rgba(139,21,56,0.4)] hover:-translate-y-0.5 flex items-center gap-4"
            >
              <div className="w-14 h-14 bg-white/15 flex items-center justify-center shrink-0">
                <Phone size={26}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/70">Teléfono</div>
                <div className="font-display text-xl font-bold leading-tight">{STORE_PHONE_DISPLAY}</div>
                <div className="text-sm text-white/80 mt-0.5">Llamada directa a tienda</div>
              </div>
            </a>
          </div>
        </section>

        {/* Tienda */}
        <section className="bg-[#2E2638] text-white p-7 sm:p-9">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#C9A961] mb-3">
            Tienda física Core Colchón
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-start gap-3 mb-3">
                <MapPin className="text-[#C9A961] shrink-0 mt-0.5" size={18}/>
                <div>
                  <div className="font-bold">C/ Dehesa de Mari Martín, 27</div>
                  <div className="text-white/70 text-sm">Autovía N-V · 28600 Navalcarnero, Madrid</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="text-[#C9A961] shrink-0 mt-0.5" size={18}/>
                <div className="text-sm text-white/80">
                  <div>Lun–Sáb: 10:00–14:00</div>
                  <div>Lun–Sáb: 17:00–20:30</div>
                </div>
              </div>
            </div>
            <div className="flex sm:flex-col items-stretch gap-3 sm:justify-center">
              <a
                href="https://maps.google.com/?q=Avda.+de+la+Dehesa+13,+Navalcarnero"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-maps"
                className="flex-1 sm:flex-none text-center px-4 py-3 bg-white text-[#2E2638] font-bold text-xs uppercase tracking-wider hover:bg-[#C9A961] hover:text-[#2E2638] transition-colors"
              >
                Cómo llegar →
              </a>
              <a
                href={`mailto:hola@corecolchon.com`}
                data-testid="cta-email"
                className="flex-1 sm:flex-none text-center px-4 py-3 border border-white/20 text-white font-bold text-xs uppercase tracking-wider hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
              >
                <Mail size={14}/> Escribir email
              </a>
            </div>
          </div>
        </section>

        {/* Tranquilidad */}
        <div className="mt-10 flex items-center justify-center gap-2 text-xs text-[#5C5366]">
          <ShieldCheck size={14} className="text-[#8B1538]"/>
          <span>Tus datos están protegidos. Solo los usaremos para coordinar tu visita.</span>
        </div>

      </main>
    </div>
  );
}
